import React, { useCallback } from "react";
import { useField } from "formik";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { ClassCombobox } from "@components/transactions/TransactionSplitV2/ClassCombobox";
import { useClasses } from "@api/class.api";
import { getDescendantIds } from "@helpers/class";
import { TransactionRuleConditionFormSchema } from "../constants";
import { getFieldNamePrefix } from "../../utils";

interface Props {
    index: number;
    controlButtonSize?: TriggerButtonProps["size"];
}

export const ClassConditionForm: React.FC<Props> = ({
    index,
    controlButtonSize,
}) => {
    const fieldName = `${getFieldNamePrefix(index)}.classIds`;
    const [field, , helpers] =
        useField<TransactionRuleConditionFormSchema["classIds"]>(fieldName);

    const { classesMap } = useClasses();

    // selectedParentClassIncludesAllChildren: true —
    // selecting a parent auto-selects all descendants; removing it removes them too.
    const handleChange = useCallback(
        (newClassIds: string[]) => {
            const prevSet = new Set(field.value ?? []);
            const newSet = new Set(newClassIds);

            const added = newClassIds.find((id) => !prevSet.has(id));
            const removed = [...prevSet].find((id) => !newSet.has(id));

            if (added) {
                getDescendantIds({ classesMap, id: added }).forEach((id) =>
                    newSet.add(id),
                );
            }
            if (removed) {
                getDescendantIds({ classesMap, id: removed }).forEach((id) =>
                    newSet.delete(id),
                );
            }

            helpers.setValue(Array.from(newSet));
        },
        [field.value, helpers, classesMap],
    );

    return (
        <ClassCombobox
            selectedClassIds={field.value ?? []}
            onChange={handleChange}
            // canSelectOnlyOneClassInTheSameRoot: false —
            // condition form allows multiple classes from the same root group
            canSelectOnlyOneClassInTheSameRoot={false}
            triggerButtonProps={{
                variant: "primary",
                size: controlButtonSize ?? "md",
            }}
        />
    );
};
